/**
 * PathwayListFallback (PROD-591).
 *
 * HC6 a11y compliance: React Flow's accessibility is limited (canvas
 * elements + custom event handling do not surface well to assistive
 * tech). The editor provides this list view as a parity surface that
 * mirrors the canvas content. Toggle is via the "Show as list" button
 * in `PathwayEditor`.
 *
 * The list groups nodes by column rank (Demographics, Vitals, Labs,
 * Decision, NBA, Outcome) so the keyboard user reads the pathway in
 * the same order as the canvas lays it out. Each row is a real
 * `<button>` so `Tab` + `Space` / `Enter` activate selection.
 *
 * HC5: zero em / en dashes.
 */
import { rankFor } from '../../services/pathway-layout';
import type { EditablePathway } from '../../state/pathway.store';
import type { PathwayNodeType } from '../../services/pathway-layout';
import { NODE_TYPE_LABELS } from './PathwayPalette';

export interface PathwayListFallbackProps {
  pathway: EditablePathway;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  className?: string;
}

// Sprint 4.3.7-fix-2: include the Awell 3-tier hierarchy types so
// canonical fixtures (now Track + Steps wrapping the legacy activity
// nodes) surface every node as a list row. Order: Track first, then
// Steps + Actions, then the legacy activity columns.
const COLUMNS: ReadonlyArray<PathwayNodeType> = [
  'track',
  'step',
  'action',
  'demographics',
  'vitals',
  'labs',
  'decision',
  'nba',
  'outcome',
];

export function PathwayListFallback({
  pathway,
  selectedNodeId,
  onSelectNode,
  className,
}: PathwayListFallbackProps) {
  return (
    <div
      data-testid="pathway-list-fallback"
      data-pathway-id={pathway.id}
      role="region"
      aria-label="Pathway list fallback"
      className={[
        'flex flex-col gap-gutter-md p-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {COLUMNS.map((column) => {
        const nodes = pathway.nodes.filter((n) => n.nodeType === column);
        if (nodes.length === 0) return null;
        return (
          <section
            key={column}
            data-testid={`pathway-list-column-${column}`}
            data-rank={rankFor(column)}
            className="flex flex-col gap-gutter-sm"
          >
            <h4 className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary">
              {NODE_TYPE_LABELS[column]}
            </h4>
            <ul role="list" className="flex flex-col gap-1">
              {nodes.map((node) => {
                const isSelected = node.id === selectedNodeId;
                return (
                  <li key={node.id}>
                    <button
                      type="button"
                      data-testid={`pathway-list-row-${node.id}`}
                      data-selected={isSelected ? 'true' : 'false'}
                      aria-current={isSelected ? 'true' : 'false'}
                      onClick={() =>
                        onSelectNode(isSelected ? null : node.id)
                      }
                      className={[
                        'flex w-full flex-col items-start gap-1 rounded-signal-md border px-gutter-md py-gutter-sm text-left text-signal-sm focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
                        isSelected
                          ? 'border-signal-primary-500 bg-signal-primary-50 text-text-primary'
                          : 'border-surface-border bg-surface-base text-text-primary hover:bg-surface-muted',
                      ].join(' ')}
                    >
                      <span className="font-semibold">{node.label}</span>
                      {node.description ? (
                        <span className="text-signal-xs text-text-tertiary">
                          {node.description}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
